import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Loader2, Trash2, Play, Video, BarChart3, Eye, Heart, MessageCircle, Phone,
} from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { toast } from "sonner";
import MobileScreenHeader from "../components/MobileScreenHeader";
import ReelAnalyticsSheet from "../components/reels/ReelAnalyticsSheet";

const STATUS_STYLE = {
    pending:   { bg: "#FEF3C7", text: "#B45309", label: "Pending" },
    approved:  { bg: "#D1FAE5", text: "#065F46", label: "Approved" },
    rejected:  { bg: "#FEE2E2", text: "#991B1B", label: "Rejected" },
    hidden:    { bg: "#E5E7EB", text: "#374151", label: "Hidden" },
};

export default function MyReels() {
    const [items, setItems]     = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(true);
    const [analyticsReel, setAnalyticsReel] = useState(null);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get("/me/reels").then((r) => r.data?.items || []).catch(() => []),
            api.get("/me/reels/summary").then((r) => r.data?.items || {}).catch(() => ({})),
        ]).then(([reels, sum]) => { setItems(reels); setSummary(sum); })
          .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const del = async (id) => {
        if (!window.confirm("Delete this reel?")) return;
        try {
            await api.delete(`/reels/${id}`);
            toast.success("Reel deleted");
            load();
        } catch { toast.error("Could not delete"); }
    };

    // Portfolio totals across all my reels
    const portfolio = items.reduce(
        (a, r) => ({
            views: a.views + (r.views || 0),
            likes: a.likes + (r.likes_count || 0),
            enquiries: a.enquiries + (summary[r.id]?.enquiries || 0),
            calls: a.calls + (summary[r.id]?.calls || 0) + (summary[r.id]?.whatsapp || 0),
        }),
        { views: 0, likes: 0, enquiries: 0, calls: 0 },
    );

    return (
        <div className="min-h-screen bg-[#f6f7fb] pb-24" data-testid="my-reels-page">
            <MobileScreenHeader title="My Reels" subtitle="Manage your GPM Reels" testid="my-reels-header" />

            <div className="px-4 mt-3">
                <Link
                    to="/reels/create"
                    data-testid="my-reels-create"
                    className="w-full inline-flex items-center justify-center gap-1 py-2.5 rounded-xl text-[12.5px] font-extrabold shadow"
                    style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                >
                    <Video className="w-3.5 h-3.5" /> Create New Reel
                </Link>
            </div>

            {/* Portfolio summary */}
            {items.length > 0 && (
                <div className="px-4 mt-4 grid grid-cols-4 gap-2" data-testid="my-reels-portfolio">
                    <PortfolioStat icon={<Eye className="w-3 h-3" />}            label="Views"     value={portfolio.views}     tint="#DBEAFE" fg="#1E40AF" />
                    <PortfolioStat icon={<Heart className="w-3 h-3" />}          label="Likes"     value={portfolio.likes}     tint="#FEE2E2" fg="#B91C1C" />
                    <PortfolioStat icon={<MessageCircle className="w-3 h-3" />}  label="Enquiries" value={portfolio.enquiries} tint="#FEF3C7" fg="#B45309" />
                    <PortfolioStat icon={<Phone className="w-3 h-3" />}          label="Contacts"  value={portfolio.calls}     tint="#D1FAE5" fg="#065F46" />
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-16">
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#002657" }} />
                </div>
            ) : items.length === 0 ? (
                <div className="px-4 mt-6 text-center rounded-2xl bg-white border p-8 mx-4"
                     style={{ borderColor: "rgba(0,38,87,0.1)" }}>
                    <div className="font-heading font-extrabold text-[14px]" style={{ color: "#002657" }}>
                        No reels yet
                    </div>
                    <div className="text-[11.5px] mt-1" style={{ color: "rgba(0,38,87,0.6)" }}>
                        Tap "Create New Reel" to promote one of your properties.
                    </div>
                </div>
            ) : (
                <div className="px-4 mt-4 space-y-3">
                    {items.map((r) => {
                        const s   = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
                        const sum = summary[r.id] || { enquiries: 0, calls: 0, whatsapp: 0 };
                        return (
                            <div key={r.id} className="rounded-2xl bg-white border shadow-sm overflow-hidden"
                                 style={{ borderColor: "rgba(0,38,87,0.08)" }}
                                 data-testid={`my-reel-${r.id}`}>
                                <div className="flex gap-3 p-3">
                                    <button
                                        onClick={() => setAnalyticsReel(r)}
                                        className="w-16 h-24 rounded-lg overflow-hidden bg-black shrink-0 relative"
                                        data-testid={`my-reel-thumb-${r.id}`}
                                    >
                                        {r.thumbnail_url ? (
                                            <img src={fileUrl(r.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <video src={fileUrl(r.video_url)} muted playsInline className="w-full h-full object-cover" />
                                        )}
                                        <div className="absolute bottom-1 right-1 w-5 h-5 rounded-full flex items-center justify-center"
                                             style={{ background: "rgba(0,0,0,0.6)" }}>
                                            <Play className="w-2.5 h-2.5 text-white" fill="white" />
                                        </div>
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-1">
                                            <div className="font-heading font-extrabold text-[12.5px] truncate flex-1" style={{ color: "#002657" }}>
                                                {r.property?.title || "Property"}
                                            </div>
                                            <span className="text-[9.5px] font-extrabold px-1.5 py-0.5 rounded shrink-0"
                                                  style={{ background: s.bg, color: s.text }}>
                                                {s.label}
                                            </span>
                                        </div>
                                        <div className="text-[10px] mt-0.5" style={{ color: "rgba(0,38,87,0.6)" }}>
                                            {(r.created_at || "").slice(0, 10)}
                                        </div>
                                        {/* Inline metrics */}
                                        <div className="mt-2 grid grid-cols-4 gap-1 text-[10px]"
                                             data-testid={`my-reel-metrics-${r.id}`}>
                                            <Metric icon={<Eye className="w-2.5 h-2.5" />}          value={r.views || 0}         fg="#1E40AF" />
                                            <Metric icon={<Heart className="w-2.5 h-2.5" />}        value={r.likes_count || 0}   fg="#B91C1C" />
                                            <Metric icon={<MessageCircle className="w-2.5 h-2.5" />} value={sum.enquiries || 0}   fg="#B45309" highlight />
                                            <Metric icon={<Phone className="w-2.5 h-2.5" />}        value={(sum.calls || 0) + (sum.whatsapp || 0)} fg="#065F46" highlight />
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-1.5 px-3 pb-3">
                                    <button
                                        onClick={() => setAnalyticsReel(r)}
                                        data-testid={`my-reel-analytics-${r.id}`}
                                        className="inline-flex items-center justify-center gap-1 py-2 rounded-lg text-[11.5px] font-extrabold"
                                        style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                                    >
                                        <BarChart3 className="w-3.5 h-3.5" /> View Analytics
                                    </button>
                                    <button
                                        onClick={() => del(r.id)}
                                        data-testid={`my-reel-delete-${r.id}`}
                                        className="inline-flex items-center justify-center gap-1 py-2 rounded-lg text-[11.5px] font-bold border"
                                        style={{ borderColor: "rgba(220,38,38,0.3)", color: "#DC2626", background: "white" }}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" /> Delete
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {analyticsReel && (
                <ReelAnalyticsSheet reel={analyticsReel} onClose={() => setAnalyticsReel(null)} />
            )}
        </div>
    );
}

function PortfolioStat({ icon, label, value, tint, fg }) {
    return (
        <div className="rounded-xl p-2 border" style={{ background: tint, borderColor: "transparent" }}>
            <div className="flex items-center gap-0.5 text-[9.5px] font-extrabold" style={{ color: fg }}>
                {icon} {label}
            </div>
            <div className="mt-0.5 font-heading font-extrabold text-[15px] leading-tight" style={{ color: fg }}>{value}</div>
        </div>
    );
}

function Metric({ icon, value, fg, highlight }) {
    return (
        <div className="inline-flex items-center gap-0.5" style={{ color: fg, fontWeight: highlight ? 800 : 600 }}>
            {icon} {value}
        </div>
    );
}
